import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import AnimeCard from '@/components/base/AnimeCard';
import { animeList, categories } from '@/mocks/anime';

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'all';
  const [activeCategory, setActiveCategory] = useState(initialType);
  const [activeTags, setActiveTags] = useState<string[]>([]);

  useEffect(() => {
    const typeParam = searchParams.get('type') || 'all';
    setActiveCategory(typeParam);
  }, [searchParams]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    animeList.forEach((a) => a.tags.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet);
  }, []);

  const filteredAnime = useMemo(() => {
    return animeList.filter((a) => {
      if (activeCategory !== 'all' && a.category !== activeCategory) return false;
      if (activeTags.length > 0 && !a.tags.some((t) => activeTags.includes(t))) return false;
      return true;
    });
  }, [activeCategory, activeTags]);

  const handleCategoryChange = (catId: string) => {
    setActiveCategory(catId);
    if (catId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ type: catId });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleTag = (tag: string) => {
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const categoryLabel = categories.find((c) => c.id === activeCategory)?.name || '全部';

  return (
    <>
      <SchemaOrg type="WebPage" data={{ name: `奶泡动漫 - ${categoryLabel}分类`, description: `奶泡动漫${categoryLabel}分类页面，浏览海量高品质${categoryLabel}番剧资源` }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '分类浏览' },
                ]}
              />
            </div>

            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              分类浏览
            </h1>
            <p className="text-sm text-foreground-500 mb-8">探索海量番剧，找到你的最爱</p>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              <span className="text-xs text-foreground-400 mr-1">标签筛选：</span>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTags.includes(tag)
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
              {activeTags.length > 0 && (
                <button
                  onClick={() => setActiveTags([])}
                  className="px-3 py-1 rounded-md text-xs text-foreground-400 hover:text-foreground-600 transition-colors whitespace-nowrap"
                >
                  清除筛选
                </button>
              )}
            </div>

            {filteredAnime.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-product-shop>
                {filteredAnime.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">没有找到匹配的番剧，试试调整筛选条件吧</p>
              </div>
            )}

            {filteredAnime.length > 0 && (
              <p className="text-center text-xs text-foreground-400 mt-8">
                共找到 {filteredAnime.length} 部番剧
              </p>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}